// Ad video sound for the v2 board.
//
// The ad plays WITH sound from the moment it starts, and is paused (and
// muted) only while a queue announcement is speaking; it resumes with sound
// when the announcement ends. Every video still starts the one way every TV
// browser renders reliably — the autoplay + muted attributes — and is
// unmuted from here once it is playing (AdsPanel calls unmuteAd on the
// `playing` event).
//
// A browser that refuses sound without a user gesture (laptop Chrome on a
// freshly loaded page) pauses an autoplaying video the moment it is unmuted,
// or rejects play(); in that case the ad keeps playing silently and sound is
// retried at the next chance: the first click/key on the page, and the end
// of every announcement. The TV browser lets v1's announcements play without
// a gesture, so it is expected to accept the unmute straight away.
//
// The ducked flag lives here so the announcer and AdsPanel never disagree:
// a video that mounts mid-announcement stays silent until it ends.
import { adSoundDisabled } from "./debugFlags";

let ducked = false;

const noop = () => {};

const playMuted = (v: HTMLVideoElement) => {
  v.muted = true;
  v.play().catch(noop);
};

// Sound on, picture kept, playback running. Safe to call on a video that is
// already playing (play() is then a no-op).
export const unmuteAd = (v: HTMLVideoElement) => {
  if (ducked || v.ended || !v.isConnected) return;
  // ?adsound=0 keeps ads silent — the A/B switch for a TV whose picture
  // disappears the moment a video is unmuted.
  if (adSoundDisabled()) {
    if (v.paused) playMuted(v);
    return;
  }
  v.muted = false;
  // Unmuted playback refused → keep the ad moving silently; sound is retried
  // on the next interaction / announcement end (see armUnmuteOnInteraction).
  const settle = () => {
    // Never resurrect a video React has already removed from the page — a
    // detached element that is told to play keeps its audio going unseen.
    if (ducked || v.ended || !v.isConnected) return;
    if (v.paused) playMuted(v);
  };
  v.play().then(settle, () => playMuted(v));
  // Some engines apply the autoplay-policy pause asynchronously.
  setTimeout(settle, 250);
};

// An announcement is speaking: freeze the ad, silently.
const pauseAd = (v: HTMLVideoElement) => {
  v.muted = true;
  v.pause();
};

const applyToVideos = () =>
  document.querySelectorAll("video").forEach((v) => {
    if (ducked) pauseAd(v);
    else unmuteAd(v);
  });

export const setAdsDucked = (on: boolean) => {
  ducked = on;
  applyToVideos();
};

export const adsDucked = () => ducked;

// Retry sound on the first real user gesture — the click on the audio
// permission box, or any click/key on the page. Harmless on a TV that never
// receives one. Armed once for the document.
let armed = false;
export const armUnmuteOnInteraction = () => {
  if (armed || typeof document === "undefined") return;
  armed = true;
  const retry = () => {
    if (!ducked) applyToVideos();
  };
  document.addEventListener("pointerdown", retry, true);
  document.addEventListener("keydown", retry, true);
};
